import React, {startTransition, useCallback, useEffect, useMemo, useState} from "react";
import { MdOutlineStar } from "react-icons/md";
import { fetchWithRefreshUser,slugify } from "../types/types";
import { Rating } from "react-simple-star-rating";
import './hookContainerRestau.css'

export const useRestaurantLink = (city: string, name: string, idUrl: string) => {
  return useMemo(() => {
    return `/restaurant/${slugify(city)}/${slugify(name)}_${idUrl}?src=home`;
  }, [city, name, idUrl]);
};

export const isNewRestaurant = (datePublished?: Date | string | null) => {
    if (!datePublished) return false;
    const now = Date.now();
    const publishedAt = new Date(datePublished).getTime();
    return (now - publishedAt) < 1000 * 60 * 60 * 24 * 14;
}
export const formatRestaurantNote = (note: number) => {
  if (Number(note) === 0) return "";

  return Number(note) % 1 === 0
    ? Number(note).toFixed(0)
    : Number(note).toFixed(1);
};
export const formatReviewLabel = (nbrReview: number) => {
  return `(${nbrReview} ${nbrReview < 2 ? "avis" : "avis"})`;
};

type UseRestaurantFavoriteParams = {
  idrestaurant: number;
  isLogin: number;
  restoFav: number[];
  setRestoFav: React.Dispatch<React.SetStateAction<number[]>>;
  setConnectionScreen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const useRestaurantFavorite = ({idrestaurant,isLogin,restoFav,setRestoFav,setConnectionScreen}: UseRestaurantFavoriteParams) => {
  const [fav, setFav] = useState(restoFav.includes(idrestaurant));
  const [possibleLike, setPossibleLike] = useState(true);

  useEffect(() => {
    setFav(restoFav.includes(idrestaurant));
  }, [restoFav, idrestaurant]);

  const handleFav = useCallback(async () => {
    if (isLogin === 0) {
      startTransition(() => {setConnectionScreen(true);});
      return;
    }

    if (!possibleLike) return;

    try {
      setPossibleLike(false);

      const token = localStorage.getItem("token");
      const response = await fetchWithRefreshUser(`${process.env.REACT_APP_DIRECTION_REQUEST}/api/updateFav`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify({idrestaurant: idrestaurant}),
        });

      if (!response) {
        throw new Error("Erreur lors de la mise à jour du favori");
      }

      setRestoFav((prev) => {
        if (prev.includes(idrestaurant)) {
          return prev.filter((id) => id !== idrestaurant);
        }

        return [...prev, idrestaurant];
      });

      setFav((prev) => !prev);

      setTimeout(() => {
        setPossibleLike(true);
      }, 1000);
    } catch (error) {
      console.error("Erreur lors de la mise à jour en base de données", error);
      setPossibleLike(true);
    }
  }, [idrestaurant,isLogin,possibleLike,setConnectionScreen,setRestoFav]);

  return {fav,handleFav};
};

const GOLD_GRADIENTS = ["#FFE78D", "#ECC868", "#D3A744", "#C49835"]

interface GoldStarProps {
  id: string | number;
  size?: number;
  className?: string;
}

export const GoldStar: React.FC<GoldStarProps> = ({ id, size = 19, className }) => {
  const gradientId = `goldGradient-${id}`;

  return (
    <>
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={GOLD_GRADIENTS[0]} />
            <stop offset="35%" stopColor={GOLD_GRADIENTS[1]} />
            <stop offset="65%" stopColor={GOLD_GRADIENTS[2]} />
            <stop offset="100%" stopColor={GOLD_GRADIENTS[3]} />
          </linearGradient>
        </defs>
      </svg>
      <MdOutlineStar className={className} size={size} style={{ fill: `url(#${gradientId})` }} />
    </>
  );
};

interface GoldRatingProps {
  id: string | number;
  value: number;
  size?: number;
  className?: string;
}


export const GoldRating: React.FC<GoldRatingProps> = ({
  id,
  value,
  size = 19,
  className
}) => {
  const gradientId = `goldRatingGradient-${id}`;

  return (
    <>
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={GOLD_GRADIENTS[0]} />
            <stop offset="35%" stopColor={GOLD_GRADIENTS[1]} />
            <stop offset="65%" stopColor={GOLD_GRADIENTS[2]} />
            <stop offset="100%" stopColor={GOLD_GRADIENTS[3]} />
          </linearGradient>
        </defs>
      </svg>

      <div
          className="goldRating"
          style={{
              "--gold-gradient": `url(#${gradientId})`
          } as React.CSSProperties}
      >  <Rating initialValue={Math.floor((value || 0) * 2) / 2} readonly size={size} allowFraction fillColor="#e5a951" fillClassName="goldRatingFilled"  className={className}/>
      </div>
    </>
  );
};


interface GoldRatingInputProps {
  id: string | number;
  value: number;
  size?: number;
  className?: string;
  onClick: (value: number) => void;
}

export const GoldRatingInput: React.FC<GoldRatingInputProps> = ({
  id,
  value,
  size = 19,
  className,
  onClick
}) => {
  const gradientId = `goldRatingInputGradient-${id}`;

  return (
    <>
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={GOLD_GRADIENTS[0]} />
            <stop offset="35%" stopColor={GOLD_GRADIENTS[1]} />
            <stop offset="65%" stopColor={GOLD_GRADIENTS[2]} />
            <stop offset="100%" stopColor={GOLD_GRADIENTS[3]} />
          </linearGradient>
        </defs>
      </svg>

      <div
        className="goldRating"
        style={{"--gold-gradient": `url(#${gradientId})`} as React.CSSProperties}
      >
        <Rating initialValue={Math.floor((value || 0) * 2) / 2} size={size} allowFraction onClick={onClick} fillColor="#e5a951" fillClassName="goldRatingFilled" className={className} />
      </div>
    </>
  );
};